// TableView.js - Fully corrected with column sorting for all columns
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Table, Tooltip } from 'antd';
import cryptoHelper from '@/utils/crypto';
import history from '@/utils/history';
import { useSelector } from 'react-redux';
import { selectAuth } from '@/redux/auth/selectors';
import determineStage from '@/utils/DetermineStage';
import InitiativePlanningSummary from '@/utils/initiativePlanningSummary';
import calculateNetRecurringBenefit from '@/utils/calculation/calculationRecurringBenefit';
import calculateOneTimeBenefit from '@/utils/calculation/calculateOneTimeBenefit';
import calculateOneTimeImplementationCost from '@/utils/calculation/calculateOneTimeImplementationCost';
import checkToFinanceApprovalStatus from '@/utils/Sort/checkTOFinanceApprovalStatus';
import checkWorkstreamLeadApprovalStatus from '@/utils/Sort/checkWorkstreamLeadApprovalStatus';
import checkWorkstreamSponsorApprovalStatus from '@/utils/Sort/checkWorkstreamSponsorApprovalStatus';
import getNetRecurringBenefitCategory from '@/utils/Sort/getNetRecurringBenefitCatagory';
import getNetRecurringBenefitChangeCategoryL0Plus from '@/utils/color/getNetRecurringBenefitCatagoryL0Plus';
import getNetRecurringBenefitChangeCategoryL1Plus from '@/utils/color/getNetRecurringBenefitCatagoryL1Plus';
import getNetRecurringBenefitChangeCategoryL2Plus from '@/utils/color/getNetRecurringBenefitCatagoryL2Plus';
import getNetRecurringBenefitChangeCategoryL3Plus from '@/utils/color/getNetRecurringBenefitCatagoryL3Plus';
import getNetRecurringBenefitChangeCategoryL4Plus from '@/utils/color/getNetRecurringBenefitCatagoryL4Plus';
import getComplianceStatus from '@/utils/Sort/complianceCheck';
import sortInitiativesByActuals from '@/utils/Sort/sortByActual';
import getImplementationCostCatagory from '@/utils/Sort/getImplementationCostCatagory';
import initialColumns from '@/utils/column/initialColumns';
import checkStrategyAnalystApprovalStatus from '@/utils/Sort/checkStrategyAnalystApprovalStatus';
import planningSummarySimplified from '@/utils/planningSummarySimplified';
import { Resizable } from 'react-resizable';
import 'react-resizable/css/styles.css';
import moment from 'moment';
import {
  L0ActualDate,
  L1ActualDate,
  L2ActualDate,
  L3ActualDate,
  L4ActualDate,
  L5ActualDate,
  SubmittedForL1PlanDate,
  SubmittedForL2PlanDate,
  SubmittedForL3PlanDate,
  SubmittedForL4PlanDate,
  SubmittedForL5PlanDate,
  L1PlanDate,
  L2PlanDate,
  L3PlanDate,
  L4PlanDate,
  L5PlanDate,
  getCancellationDate,
  getQualitativeKPI,
  L1OriginalPlanDate,
  L5OriginalPlanDate,
  L4OriginalPlanDate,
  L3OriginalPlanDate,
  L2OriginalPlanDate,
} from '@/utils/Sort/checkDate/getDate';
import groupInitiativesByYearActual from '@/utils/Sort/groupInitiativesByYearActual';
import { useHistory, useLocation } from 'react-router-dom/cjs/react-router-dom';

const ResizableTitle = (props) => {
  const { onResize, width, ...restProps } = props;

  if (!width) {
    return <th {...restProps} />;
  }

  return (
    <Resizable
      width={width}
      height={0}
      handle={
        <span
          className="react-resizable-handle"
          onClick={(e) => {
            e.stopPropagation();
          }}
          style={{
            position: 'absolute',
            right: 0,
            bottom: 0,
            zIndex: 1,
            width: '10px',
            height: '100%',
            cursor: 'col-resize',
            background: '#1890ff',
            opacity: 0,
            transition: 'opacity 0.3s',
            borderRadius: '2px',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.opacity = '0.5';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.opacity = '0';
          }}
        />
      }
      onResize={onResize}
      draggableOpts={{ enableUserSelectHack: false }}
    >
      <th {...restProps} />
    </Resizable>
  );
};

const TableView = ({
  initiatives,
  otherElementRef,
  colorByAttribute,
  initiativeColors,
  visibleColumn,
  setCurrentFilter,
  setFilterVisible,
  currentFilter,
  setFilterHistory,
  sortField: externalSortField,
  sortOrder: externalSortOrder
}) => {
  const history = useHistory();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const initialPage = parseInt(queryParams.get('page')) || 1;
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [internalSortField, setInternalSortField] = useState(null);
  const [internalSortOrder, setInternalSortOrder] = useState(null);
  const currentUser = useSelector(selectAuth);

  // Use external sort if provided, otherwise use internal
  const sortField = externalSortField || internalSortField;
  const sortOrder = externalSortOrder || internalSortOrder;

  // State for column widths
  const [columnWidths, setColumnWidths] = useState({});

  // Filter initiatives based on user access
  const myInitiatives = useMemo(() => {
    if (!initiatives || !Array.isArray(initiatives)) return [];

    return initiatives.filter((item) => {
      if (currentUser?.hasSpecialAccess === true) {
        return true;
      }
      const isOwner = item.initiativeOwnerInfo?.[0]?._id === currentUser.id;
      const isBackupOwner = item.initiativeOwnerBackupInfo?.[0]?._id === currentUser.id;
      const isWorkstreamLead = item.workstreamLeadInfo?.[0]?._id === currentUser.id;
      const isWorkstreamSponsor = item.workstreamSponsorInfo?.[0]?._id === currentUser.id;
      const isAllowedUser = item.AllowOnlyTo?.some(
        (allowedUser) => allowedUser._id === currentUser.id
      );

      const isAdminRole =
        currentUser.role.name === 'CTO' ||
        currentUser.role.name === 'TO Finance' ||
        currentUser.role.name === 'Strategy Analyst' ||
        currentUser.role.name === 'Admin';

      const hasAccess =
        isOwner ||
        isBackupOwner ||
        isWorkstreamLead ||
        isWorkstreamSponsor ||
        isAllowedUser ||
        isAdminRole;

      return hasAccess;
    });
  }, [initiatives, currentUser]);

  // Helper function to safely convert any value to string for rendering
  const safeToString = useCallback((value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'number') return value.toString();
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (Array.isArray(value)) {
      return value.map(item => safeToString(item)).join(', ');
    }
    if (typeof value === 'object') {
      if (value.firstName || value.lastName) {
        return `${value.firstName || ''} ${value.lastName || ''}`.trim();
      }
      if (value.name) return value.name;
      if (value.strategyName) return value.strategyName;
      if (value.workstreamName) return value.workstreamName;
      if (value.subworkstreamName) return value.subworkstreamName;
      try {
        return JSON.stringify(value);
      } catch {
        return '';
      }
    }
    return String(value);
  }, []);

  // Render with color and proper text truncation (2 lines max, shows full content when column is widened)
  const renderWithColor = useCallback((value, color, isExpandable = false) => {
    const stringValue = safeToString(value);

    if (isExpandable) {
      return (
        <div style={{ display: 'flex', alignItems: 'flex-start', width: '100%', minWidth: 0 }}>
          {color && (
            <span
              style={{
                width: '12px',
                height: '12px',
                borderRadius: '2px',
                backgroundColor: color,
                marginRight: '8px',
                marginTop: '2px',
                display: 'inline-block',
                flexShrink: 0,
              }}
            />
          )}
          <div
            style={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              wordBreak: 'break-word',
              wordWrap: 'break-word',
              width: '100%',
              minWidth: 0,
              flex: 1,
              lineHeight: '1.5em',
              maxHeight: '3em',
            }}
            title={stringValue}
          >
            {stringValue}
          </div>
        </div>
      );
    }

    return (
      <div style={{ display: 'flex', alignItems: 'center', width: '100%', minWidth: 0 }}>
        {color && (
          <span
            style={{
              width: '12px',
              height: '12px',
              borderRadius: '2px',
              backgroundColor: color,
              marginRight: '8px',
              display: 'inline-block',
              flexShrink: 0,
            }}
          />
        )}
        <div
          style={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            width: '100%',
            minWidth: 0,
            flex: 1,
          }}
        >
          {stringValue}
        </div>
      </div>
    );
  }, [safeToString]);

  useEffect(() => {
    const page = parseInt(new URLSearchParams(location.search).get('page')) || 1;
    setCurrentPage(page);
  }, [location.search]);

  const handleTableChange = (pagination, filters, sorter) => {
    setCurrentPage(pagination.current);
    history.push(`/?section=workbook&page=${pagination.current}`);

    // Update internal sort state when user clicks on column headers
    if (sorter.field) {
      setInternalSortField(sorter.field);
      setInternalSortOrder(sorter.order);
    }
  };

  const safeSorter = (getDateFn) => (a, b) => {
    const dateA = getDateFn(a);
    const dateB = getDateFn(b);

    const timeA = dateA instanceof Date && !isNaN(dateA) ? dateA.getTime() : 0;
    const timeB = dateB instanceof Date && !isNaN(dateB) ? dateB.getTime() : 0;

    return timeA - timeB;
  };
  const renderDateColumn = useCallback((record, getDateFn, colorAttributes, colorByAttribute, initiativeColors) => {
    const date = getDateFn(record);
    const isValidDate = date instanceof Date && !isNaN(date);

    let displayDate = 'N/A';
    if (isValidDate) {
      // Force UTC display to match both table and Excel
      const year = date.getUTCFullYear();
      const month = date.getUTCMonth();
      const day = date.getUTCDate();
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      displayDate = `${day} ${monthNames[month]} ${year}`;
    }

    const dateKey = isValidDate ?
      `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}` :
      'Not Set';
    const shouldColor = colorAttributes.includes(colorByAttribute);
    const color = shouldColor ? initiativeColors?.[dateKey] : null;

    return renderWithColor(displayDate, color, false);
  }, [renderWithColor]);

  const getFullName = (users) => {
    if (!users || !Array.isArray(users)) return 'Not Assigned';
    return users.map(u => `${u?.firstName || ''} ${u?.lastName || ''}`.trim()).filter(Boolean).join(', ') || 'Not Assigned';
  };

  const getWorkstreamName = (workstreams) => {
    if (!workstreams || !Array.isArray(workstreams)) return 'Not Assigned';
    return workstreams.map(ws => ws?.workstreamName || ws?.subworkstreamName || '<not set>').filter(Boolean).join(', ') || 'Not Assigned';
  };

  const getSectorSpecificName = (sectors) => {
    if (!sectors || !Array.isArray(sectors)) return 'Not Assigned';
    const sectorNames = sectors.map(s => s?.sectorName).filter(Boolean);
    return sectorNames.length > 0 ? sectorNames.join(', ') : 'Not Assigned';
  };

  const getSubsectorSpecificName = (subsectors) => {
    if (!subsectors || !Array.isArray(subsectors)) return 'Not Assigned';
    const subsectorNames = subsectors.map(s => s?.subsectorName).filter(Boolean);
    return subsectorNames.length > 0 ? subsectorNames.join(', ') : 'Not Assigned';
  };

  const getStrategyNames = (strategyInfo) => {
    if (!strategyInfo || !Array.isArray(strategyInfo)) return 'Have not strategy';
    return strategyInfo.map(s => s?.strategyName).filter(Boolean).join(', ') || 'Have not strategy';
  };

  // Handle column resize
  const handleResize = useCallback((key) => (e, { size }) => {
    setColumnWidths(prev => ({
      ...prev,
      [key]: size.width
    }));
  }, []);

  const columns = useMemo(() => {
    return initialColumns.map(col => {
      const newCol = { ...col };

      // Set initial width from columnWidths state or default
      let defaultWidth = col.width || 150;
      if (col.key === 'initiativeName') {
        defaultWidth = 250;
      } else if (col.key === 'initiativeNumber - initiativeName') {
        defaultWidth = 200;
      } else if (col.key === 'Description') {
        defaultWidth = 250;
      }

      newCol.width = columnWidths[col.key] || defaultWidth;

      newCol.filterDropdown = () => null;
      newCol.onFilterDropdownVisibleChange = (visible) => {
        if (visible) {
          setFilterVisible(true);
          setCurrentFilter(col.key);
          setFilterHistory((prevHistory) => [...prevHistory, col.key, 'Filter Options']);
        }
      };

      // Add resize functionality to column
      newCol.onHeaderCell = (column) => ({
        width: column.width,
        onResize: handleResize(column.key),
      });

      // Set ellipsis to false to allow proper text wrapping
      newCol.ellipsis = false;

      // Enable sorting by default for all columns
      newCol.sortable = true;

      switch (col.key) {
        case 'initiativeNumber':
          newCol.sorter = (a, b) => {
            const aNum = parseInt(a.initiativeNumber) || 0;
            const bNum = parseInt(b.initiativeNumber) || 0;
            return aNum - bNum;
          };
          if (!sortField && col.key === 'initiativeNumber') {
            newCol.defaultSortOrder = 'descend';
          }
          newCol.render = (_, record) => {
            const nameNumber = record.initiativeNumber;
            const color = colorByAttribute === 'initiativeNumber' ? initiativeColors[nameNumber] : null;
            return renderWithColor(nameNumber, color, false);
          };
          break;
        case 'initiativeName':
          newCol.sorter = (a, b) => (a.initiativeName || '').localeCompare(b.initiativeName || '');
          newCol.render = (text, record) => {
            const color = colorByAttribute === 'initiativeName' ? initiativeColors[record.initiativeName] : null;
            return renderWithColor(text, color, true);
          };
          break;
        case 'forSpecificCustomer':
          newCol.sorter = (a, b) => {
            const valA = a.forSpecificCustomer ? 1 : 0;
            const valB = b.forSpecificCustomer ? 1 : 0;
            return valA - valB;
          };
          newCol.render = (_, record) => {
            const value = record.forSpecificCustomer ? 'Yes' : 'No';
            const color = colorByAttribute === 'forSpecificCustomer' ? initiativeColors[value] : null;
            return renderWithColor(value, color, false);
          };
          break;
        case 'segmentalPBT':
          newCol.sorter = (a, b) => {
            const valA = a.segmentalPBT ? 1 : 0;
            const valB = b.segmentalPBT ? 1 : 0;
            return valA - valB;
          };
          newCol.render = (_, record) => {
            const value = record.segmentalPBT ? 'Yes' : 'No';
            const color = colorByAttribute === 'segmentalPBT' ? initiativeColors[value] : null;
            return renderWithColor(value, color, false);
          };
          break;
        case 'weeklyStatus':
          newCol.sorter = (a, b) => (a.weeklyStatus || '').localeCompare(b.weeklyStatus || '');
          newCol.render = (text, record) => {
            const color = colorByAttribute === 'weeklyStatus' ? initiativeColors[record.weeklyStatus] : null;
            return renderWithColor(text, color, false);
          };
          break;
        case 'segment':
          newCol.sorter = (a, b) => (a.segment || '').localeCompare(b.segment || '');
          newCol.render = (text, record) => {
            const color = colorByAttribute === 'segment' ? initiativeColors[record.segment] : null;
            return renderWithColor(text, color, false);
          };
          break;
        case 'initiativeCategory':
          newCol.sorter = (a, b) => (a.initiativeCategory || '').localeCompare(b.initiativeCategory || '');
          newCol.render = (text, record) => {
            const color = colorByAttribute === 'initiativeCategory' ? initiativeColors[record.initiativeCategory] : null;
            return renderWithColor(text, color, false);
          };
          break;
        case 'type':
          newCol.sorter = (a, b) => (a.type || '').localeCompare(b.type || '');
          newCol.render = (text, record) => {
            const color = colorByAttribute === 'type' ? initiativeColors[record.type] : null;
            return renderWithColor(text, color, false);
          };
          break;
        case 'size':
          newCol.sorter = (a, b) => (a.size || '').localeCompare(b.size || '');
          newCol.render = (text, record) => {
            const color = colorByAttribute === 'size' ? initiativeColors[record.size] : null;
            return renderWithColor(text, color, false);
          };
          break;
        case 'stageSimplified':
          newCol.sorter = (a, b) => (determineStage(a) || '').localeCompare(determineStage(b) || '');
          newCol.render = (_, record) => {
            const stageValue = determineStage(record);
            let displayText = stageValue;
            if (stageValue === 'L0') displayText = 'L0(Idea)';
            else if (stageValue === 'L1') displayText = 'L1(Identified)';
            else if (stageValue === 'L2') displayText = 'L2(Validated)';
            else if (stageValue === 'L3') displayText = 'L3(Planned)';
            else if (stageValue === 'L4') displayText = 'L4(Executed)';
            else if (stageValue === 'L5') displayText = 'L5(Realized)';
            const color = colorByAttribute === 'Stage' ? initiativeColors[stageValue] : null;
            return renderWithColor(displayText, color, false);
          };
          break;
        case 'stage':
          newCol.sorter = (a, b) => (a.stage || '').localeCompare(b.stage || '');
          newCol.render = (_, record) => {
            const stageValue = record.stage || '';
            let displayText;
            switch (stageValue) {
              case 'L0': displayText = 'L0 (Idea)'; break;
              case 'L1': displayText = 'L1 (Identified)'; break;
              case 'L2': displayText = 'L2 (Validated)'; break;
              case 'L3': displayText = 'L3 (Planned)'; break;
              case 'L4': displayText = 'L4 (Executed)'; break;
              case 'L5': displayText = 'L5 (Realized)'; break;
              default: displayText = stageValue || 'No value';
            }
            const color = colorByAttribute?.toLowerCase() === 'stage (simplified)'.toLowerCase()
              ? initiativeColors[stageValue] || null
              : null;
            return renderWithColor(displayText, color, false);
          };
          break;
        case 'initiativeOwnerInfo':
        case 'initiativeOwnerBackupInfo':
        case 'workstreamLeadInfo':
        case 'workstreamSponsorInfo':
        case 'AllowOnlyTo':
          newCol.sorter = (a, b) => getFullName(a[col.dataIndex]).localeCompare(getFullName(b[col.dataIndex]));
          newCol.render = (users) => {
            const name = getFullName(users);
            const color = colorByAttribute === col.key && users?.[0] ? initiativeColors[`${users[0]?.firstName} ${users[0]?.lastName}`.trim()] : null;
            return renderWithColor(name, color, false);
          };
          break;
        case 'workstreamSponsorWorkstream':
        case 'crossFunctionalSupportWorkstreamInfo':
        case 'workstreamLeadSubworkstream':
          newCol.sorter = (a, b) => getWorkstreamName(a[col.dataIndex]).localeCompare(getWorkstreamName(b[col.dataIndex]));
          newCol.render = (workstreams) => {
            const name = getWorkstreamName(workstreams);
            const color = colorByAttribute === col.key && workstreams?.[0] ? initiativeColors[workstreams[0]?.workstreamName || workstreams[0]?.subworkstreamName] : null;
            return renderWithColor(name, color, false);
          };
          break;
        case 'sectorInfo':
          newCol.sorter = (a, b) => {
            const aSector = getSectorSpecificName(a.sectorInfo);
            const bSector = getSectorSpecificName(b.sectorInfo);
            return aSector.localeCompare(bSector);
          };
          newCol.render = (_, record) => {
            const sectors = record.sectorInfo;
            const sectorName = getSectorSpecificName(sectors);
            let color = null;
            if (colorByAttribute === 'sectorInfo' && sectors && sectors[0]) {
              const keyName = sectors[0].sectorName;
              if (keyName) {
                color = initiativeColors[keyName];
              }
            }
            return renderWithColor(sectorName, color, false);
          };
          break;
        case 'subsectorInfo':
          newCol.sorter = (a, b) => {
            const aSubsector = getSubsectorSpecificName(a.subsectorInfo);
            const bSubsector = getSubsectorSpecificName(b.subsectorInfo);
            return aSubsector.localeCompare(bSubsector);
          };
          newCol.render = (_, record) => {
            const subsectors = record.subsectorInfo;
            const subsectorName = getSubsectorSpecificName(subsectors);
            let color = null;
            if (colorByAttribute === 'subsectorInfo' && subsectors && subsectors[0]) {
              const keyName = subsectors[0].subsectorName;
              if (keyName) {
                color = initiativeColors[keyName];
              }
            }
            return renderWithColor(subsectorName, color, false);
          };
          break;
        case 'strategyInfo':
          newCol.sorter = (a, b) => getStrategyNames(a.strategyInfo).localeCompare(getStrategyNames(b.strategyInfo));
          newCol.render = (_, record) => {
            const strategyNames = getStrategyNames(record.strategyInfo);
            const color = colorByAttribute === 'strategyInfo' ? initiativeColors[strategyNames] : null;
            return renderWithColor(strategyNames, color, false);
          };
          break;
        case 'netRecurringBenefit':
          newCol.sorter = (a, b) => calculateNetRecurringBenefit(a) - calculateNetRecurringBenefit(b);
          newCol.render = (_, record) => {
            const netBenefit = parseFloat(calculateNetRecurringBenefit(record)).toFixed(3);
            let color = null;
            if (colorByAttribute === 'netRecurringBenefit') {
              color = initiativeColors[netBenefit];
            }
            return renderWithColor(netBenefit, color, false);
          };
          break;
        case 'oneTimeBenefit':
          newCol.sorter = (a, b) => calculateOneTimeBenefit(a) - calculateOneTimeBenefit(b);
          newCol.render = (_, record) => {
            const value = calculateOneTimeBenefit(record);
            const color = colorByAttribute === 'oneTimeBenefit' ? initiativeColors[value] : null;
            return renderWithColor(value, color, false);
          };
          break;
        case 'oneTimeImplementationCost':
          newCol.sorter = (a, b) => calculateOneTimeImplementationCost(a) - calculateOneTimeImplementationCost(b);
          newCol.render = (_, record) => {
            const value = calculateOneTimeImplementationCost(record);
            const color = colorByAttribute === 'oneTimeImplementationCost' ? initiativeColors[value] : null;
            return renderWithColor(value, color, false);
          };
          break;
        case 'timelineL3':
        case 'timelineL4':
          newCol.sorter = (a, b) => {
            const stage = col.key === 'timelineL3' ? 'L3' : 'L4';
            const aDate = a.timeline?.find(t => t.stage === stage)?.date?.plan;
            const bDate = b.timeline?.find(t => t.stage === stage)?.date?.plan;
            return (new Date(aDate || 0).getTime() - new Date(bDate || 0).getTime());
          };
          newCol.render = (timeline) => {
            const stage = col.key === 'timelineL3' ? 'L3' : 'L4';
            const dateEntry = Array.isArray(timeline) ? timeline.find(t => t.stage === stage) : null;
            const value = dateEntry?.date?.plan ? new Date(dateEntry.date.plan).toLocaleDateString() : 'No Date';
            const color = colorByAttribute === col.key ? initiativeColors[value] : null;
            return renderWithColor(value, color, false);
          };
          break;
        case 'Planning summary':
          newCol.sorter = (a, b) => {
            const aSummary = InitiativePlanningSummary(a);
            const bSummary = InitiativePlanningSummary(b);
            return (aSummary.planningStatus || '').localeCompare(bSummary.planningStatus || '');
          };
          newCol.render = (_, record) => {
            const planningSummary = InitiativePlanningSummary(record);
            const planningStatus = planningSummary.planningStatus;
            let color = null;
            if (colorByAttribute === 'planningSummary') {
              color = initiativeColors[planningStatus];
            }
            return renderWithColor(planningStatus, color, false);
          };
          break;
        case 'Planning summary simplified':
          newCol.sorter = (a, b) => {
            const aSummary = planningSummarySimplified(a);
            const bSummary = planningSummarySimplified(b);
            return (aSummary || '').localeCompare(bSummary);
          };
          newCol.render = (_, record) => {
            const planningSummary = planningSummarySimplified(record);
            let color = null;
            if (colorByAttribute === 'Planning summary (simplified)') {
              color = initiativeColors[planningSummary];
            }
            return renderWithColor(planningSummary, color, false);
          };
          break;
        case 'Workstream lead pending approval':
          newCol.sorter = (a, b) => (checkWorkstreamLeadApprovalStatus(a) || '').localeCompare(checkWorkstreamLeadApprovalStatus(b) || '');
          newCol.render = (_, record) => {
            const approvalStatus = checkWorkstreamLeadApprovalStatus(record);
            let color = null;
            if (colorByAttribute === 'Workstream Lead pending approval?') {
              color = initiativeColors[approvalStatus];
            }
            return renderWithColor(approvalStatus, color, false);
          };
          break;
        case 'Workstream sponsor pending approval':
          newCol.sorter = (a, b) => (checkWorkstreamSponsorApprovalStatus(a) || '').localeCompare(checkWorkstreamSponsorApprovalStatus(b) || '');
          newCol.render = (_, record) => {
            const approvalStatus = checkWorkstreamSponsorApprovalStatus(record);
            let color = null;
            if (colorByAttribute === 'Workstream Sponsor pending approval?') {
              color = initiativeColors[approvalStatus];
            }
            return renderWithColor(approvalStatus, color, false);
          };
          break;
        case 'TO finance pending approval':
          newCol.sorter = (a, b) => (checkToFinanceApprovalStatus(a) || '').localeCompare(checkToFinanceApprovalStatus(b) || '');
          newCol.render = (_, record) => {
            const approvalStatus = checkToFinanceApprovalStatus(record);
            let color = null;
            if (colorByAttribute === 'Finance lead pending approval?') {
              color = initiativeColors[approvalStatus];
            }
            return renderWithColor(approvalStatus, color, false);
          };
          break;
        case 'Strategy analyst pending approval':
          newCol.sorter = (a, b) => (checkStrategyAnalystApprovalStatus(a) || '').localeCompare(checkStrategyAnalystApprovalStatus(b) || '');
          newCol.render = (_, record) => {
            const approvalStatus = checkStrategyAnalystApprovalStatus(record);
            let color = null;
            if (colorByAttribute === 'Strategy analyst pending approval?') {
              color = initiativeColors[approvalStatus];
            }
            return renderWithColor(approvalStatus, color, false);
          };
          break;
        case 'Future implementation costs range':
          newCol.sorter = (a, b) => (getImplementationCostCatagory(a) || '').localeCompare(getImplementationCostCatagory(b) || '');
          newCol.render = (_, record) => {
            const costCategory = getImplementationCostCatagory(record);
            let color = null;
            if (colorByAttribute === 'Future implementation costs range') {
              color = initiativeColors[costCategory];
            }
            return renderWithColor(costCategory, color, false);
          };
          break;
        case 'Pipeline weekly variance (L0+)':
          newCol.sorter = (a, b) => (getNetRecurringBenefitChangeCategoryL0Plus(a) || '').localeCompare(getNetRecurringBenefitChangeCategoryL0Plus(b) || '');
          newCol.render = (_, record) => {
            const changeCategory = getNetRecurringBenefitChangeCategoryL0Plus(record);
            let color = null;
            if (colorByAttribute === 'Pipeline weekly variance (L0+)') {
              color = initiativeColors[changeCategory];
            }
            return renderWithColor(changeCategory, color, false);
          };
          break;
        case 'Pipeline weekly variance (L1+)':
          newCol.sorter = (a, b) => (getNetRecurringBenefitChangeCategoryL1Plus(a) || '').localeCompare(getNetRecurringBenefitChangeCategoryL1Plus(b) || '');
          newCol.render = (_, record) => {
            const changeCategory = getNetRecurringBenefitChangeCategoryL1Plus(record);
            let color = null;
            if (colorByAttribute === 'Pipeline weekly variance (L1+)') {
              color = initiativeColors[changeCategory];
            }
            return renderWithColor(changeCategory, color, false);
          };
          break;
        case 'Pipeline weekly variance (L2+)':
          newCol.sorter = (a, b) => (getNetRecurringBenefitChangeCategoryL2Plus(a) || '').localeCompare(getNetRecurringBenefitChangeCategoryL2Plus(b) || '');
          newCol.render = (_, record) => {
            const changeCategory = getNetRecurringBenefitChangeCategoryL2Plus(record);
            let color = null;
            if (colorByAttribute === 'Pipeline weekly variance (L2+)') {
              color = initiativeColors[changeCategory];
            }
            return renderWithColor(changeCategory, color, false);
          };
          break;
        case 'Pipeline weekly variance (L3+)':
          newCol.sorter = (a, b) => (getNetRecurringBenefitChangeCategoryL3Plus(a) || '').localeCompare(getNetRecurringBenefitChangeCategoryL3Plus(b) || '');
          newCol.render = (_, record) => {
            const changeCategory = getNetRecurringBenefitChangeCategoryL3Plus(record);
            let color = null;
            if (colorByAttribute === 'Pipeline weekly variance (L3+)') {
              color = initiativeColors[changeCategory];
            }
            return renderWithColor(changeCategory, color, false);
          };
          break;
        case 'Pipeline weekly variance (L4+)':
          newCol.sorter = (a, b) => (getNetRecurringBenefitChangeCategoryL4Plus(a) || '').localeCompare(getNetRecurringBenefitChangeCategoryL4Plus(b) || '');
          newCol.render = (_, record) => {
            const changeCategory = getNetRecurringBenefitChangeCategoryL4Plus(record);
            let color = null;
            if (colorByAttribute === 'Pipeline weekly variance (L4+)') {
              color = initiativeColors[changeCategory];
            }
            return renderWithColor(changeCategory, color, false);
          };
          break;
        case 'Actual impact update check (for last completed and previous period)':
          newCol.sorter = (a, b) => (sortInitiativesByActuals(a, 'actualImpactUpdateCheck') || '').localeCompare(sortInitiativesByActuals(b, 'actualImpactUpdateCheck') || '');
          newCol.render = (_, record) => {
            const checkStatus = sortInitiativesByActuals(record, 'actualImpactUpdateCheck');
            let color = null;
            if (colorByAttribute === 'actualImpactUpdateCheck') {
              color = initiativeColors[checkStatus];
            }
            return renderWithColor(checkStatus, color, false);
          };
          break;
        case 'Actual KPI update check (for last completed and previous period)':
          newCol.sorter = (a, b) => (sortInitiativesByActuals(a, 'actualKpiUpdateCheck') || '').localeCompare(sortInitiativesByActuals(b, 'actualKpiUpdateCheck') || '');
          newCol.render = (_, record) => {
            const checkStatus = sortInitiativesByActuals(record, 'actualKpiUpdateCheck');
            let color = null;
            if (colorByAttribute === 'actualKpiUpdateCheck') {
              color = initiativeColors[checkStatus];
            }
            return renderWithColor(checkStatus, color, false);
          };
          break;
        case '(FY23-24) Actual impact update check':
          newCol.sorter = (a, b) => (groupInitiativesByYearActual(a, '(FY23-24) Actual impact update check') || '').localeCompare(groupInitiativesByYearActual(b, '(FY23-24) Actual impact update check') || '');
          newCol.render = (_, record) => {
            const checkStatus = groupInitiativesByYearActual(record, '(FY23-24) Actual impact update check');
            let color = null;
            if (colorByAttribute === '(FY23-24) Actual impact update check') {
              color = initiativeColors[checkStatus];
            }
            return renderWithColor(checkStatus, color, false);
          };
          break;
        case '(FY24-25) Actual impact update check':
          newCol.sorter = (a, b) => (groupInitiativesByYearActual(a, '(FY24-25) Actual impact update check') || '').localeCompare(groupInitiativesByYearActual(b, '(FY24-25) Actual impact update check') || '');
          newCol.render = (_, record) => {
            const checkStatus = groupInitiativesByYearActual(record, '(FY24-25) Actual impact update check');
            let color = null;
            if (colorByAttribute === '(FY24-25) Actual impact update check') {
              color = initiativeColors[checkStatus];
            }
            return renderWithColor(checkStatus, color, false);
          };
          break;
        case 'L3 date compliance check (forecast vs planned date)':
          newCol.sorter = (a, b) => {
            const aStatus = getComplianceStatus(a, 'L3').join('');
            const bStatus = getComplianceStatus(b, 'L3').join('');
            return aStatus.localeCompare(bStatus);
          };
          newCol.render = (_, record) => {
            const complianceStatus = getComplianceStatus(record, 'L3').join('');
            const color = colorByAttribute === 'L3 date compliance check (forecast vs planned date)' ? initiativeColors[complianceStatus] : null;
            return renderWithColor(complianceStatus, color, false);
          };
          break;
        case 'L4 date compliance check (forecast vs planned date)':
          newCol.sorter = (a, b) => {
            const aStatus = getComplianceStatus(a, 'L4').join('');
            const bStatus = getComplianceStatus(b, 'L4').join('');
            return aStatus.localeCompare(bStatus);
          };
          newCol.render = (_, record) => {
            const complianceStatus = getComplianceStatus(record, 'L4').join('');
            const color = colorByAttribute === 'L4 date compliance check (forecast vs planned date)' ? initiativeColors[complianceStatus] : null;
            return renderWithColor(complianceStatus, color, false);
          };
          break;
        case 'hasFinancialImpact':
          newCol.filters = [{ text: 'Yes', value: true }, { text: 'No', value: false }];
          newCol.onFilter = (value, record) => record.hasFinancialImpact === value;
          newCol.sorter = (a, b) => {
            const valA = a.hasFinancialImpact ? 1 : 0;
            const valB = b.hasFinancialImpact ? 1 : 0;
            return valA - valB;
          };
          newCol.render = (value) => {
            let color = null;
            if (colorByAttribute === 'hasFinancialImpact') {
              color = initiativeColors[value ? 'Yes' : 'No'];
            }
            return renderWithColor(value ? 'Yes' : 'No', color, false);
          };
          break;
        case 'L1 Original Planned Date':
          newCol.sorter = safeSorter(L1OriginalPlanDate);
          newCol.render = (_, record) =>
            renderDateColumn(record, L1OriginalPlanDate, ['L1 Original Planned Date'], colorByAttribute, initiativeColors);
          break;
        case 'L2 Original Planned Date':
          newCol.sorter = safeSorter(L2OriginalPlanDate);
          newCol.render = (_, record) =>
            renderDateColumn(record, L2OriginalPlanDate, ['L2 Original Planned Date'], colorByAttribute, initiativeColors);
          break;
        case 'L3 Original Planned Date':
          newCol.sorter = safeSorter(L3OriginalPlanDate);
          newCol.render = (_, record) =>
            renderDateColumn(record, L3OriginalPlanDate, ['L3 Original Planned Date'], colorByAttribute, initiativeColors);
          break;
        case 'L4 Original Planned Date':
          newCol.sorter = safeSorter(L4OriginalPlanDate);
          newCol.render = (_, record) =>
            renderDateColumn(record, L4OriginalPlanDate, ['L4 Original Planned Date'], colorByAttribute, initiativeColors);
          break;
        case 'L5 Original Planned Date':
          newCol.sorter = safeSorter(L5OriginalPlanDate);
          newCol.render = (_, record) =>
            renderDateColumn(record, L5OriginalPlanDate, ['L5 Original Planned Date'], colorByAttribute, initiativeColors);
          break;
        case 'L1 planned date':
          newCol.sorter = safeSorter(L1PlanDate);
          newCol.render = (_, record) =>
            renderDateColumn(record, L1PlanDate, ['l1PlannedDate'], colorByAttribute, initiativeColors);
          break;
        case 'L2 planned date':
          newCol.sorter = safeSorter(L2PlanDate);
          newCol.render = (_, record) =>
            renderDateColumn(record, L2PlanDate, ['l2PlannedDate', 'l2LatestEstimatedDate', 'l2ForecastDate'], colorByAttribute, initiativeColors);
          break;
        case 'L3 planned date':
          newCol.sorter = safeSorter(L3PlanDate);
          newCol.render = (_, record) =>
            renderDateColumn(record, L3PlanDate, ['l3PlannedDate', 'l3LatestEstimatedDate', 'l3ForecastDate'], colorByAttribute, initiativeColors);
          break;
        case 'L4 planned date':
          newCol.sorter = safeSorter(L4PlanDate);
          newCol.render = (_, record) =>
            renderDateColumn(record, L4PlanDate, ['l4PlannedDate', 'l4LatestEstimatedDate', 'l4ForecastDate'], colorByAttribute, initiativeColors);
          break;
        case 'L5 planned date':
          newCol.sorter = safeSorter(L5PlanDate);
          newCol.render = (_, record) =>
            renderDateColumn(record, L5PlanDate, ['l5PlannedDate', 'l5LatestEstimatedDate', 'l5ForecastDate'], colorByAttribute, initiativeColors);
          break;
        case 'L0 actual date':
          newCol.sorter = safeSorter(L0ActualDate);
          newCol.render = (_, record) =>
            renderDateColumn(record, L0ActualDate, ['l0ActualDate'], colorByAttribute, initiativeColors);
          break;
        case 'L1 actual date':
          newCol.sorter = safeSorter(L1ActualDate);
          newCol.render = (_, record) =>
            renderDateColumn(record, L1ActualDate, ['l1ActualDate'], colorByAttribute, initiativeColors);
          break;
        case 'L2 actual date':
          newCol.sorter = safeSorter(L2ActualDate);
          newCol.render = (_, record) =>
            renderDateColumn(record, L2ActualDate, ['l2ActualDate'], colorByAttribute, initiativeColors);
          break;
        case 'L3 actual date':
          newCol.sorter = safeSorter(L3ActualDate);
          newCol.render = (_, record) =>
            renderDateColumn(record, L3ActualDate, ['l3ActualDate'], colorByAttribute, initiativeColors);
          break;
        case 'L4 actual date':
          newCol.sorter = safeSorter(L4ActualDate);
          newCol.render = (_, record) =>
            renderDateColumn(record, L4ActualDate, ['l4ActualDate'], colorByAttribute, initiativeColors);
          break;
        case 'L5 actual date':
          newCol.sorter = safeSorter(L5ActualDate);
          newCol.render = (_, record) =>
            renderDateColumn(record, L5ActualDate, ['l5ActualDate'], colorByAttribute, initiativeColors);
          break;
        case 'Sub . for L1 approv . date':
          newCol.sorter = safeSorter(SubmittedForL1PlanDate);
          newCol.render = (_, record) =>
            renderDateColumn(record, SubmittedForL1PlanDate, ['subForL1ApprovDate'], colorByAttribute, initiativeColors);
          break;
        case 'Sub . for L2 approv . date':
          newCol.sorter = safeSorter(SubmittedForL2PlanDate);
          newCol.render = (_, record) =>
            renderDateColumn(record, SubmittedForL2PlanDate, ['subForL2ApprovDate'], colorByAttribute, initiativeColors);
          break;
        case 'Sub . for L3 approv . date':
          newCol.sorter = safeSorter(SubmittedForL3PlanDate);
          newCol.render = (_, record) =>
            renderDateColumn(record, SubmittedForL3PlanDate, ['subForL3ApprovDate'], colorByAttribute, initiativeColors);
          break;
        case 'Sub . for L4 approv . date':
          newCol.sorter = safeSorter(SubmittedForL4PlanDate);
          newCol.render = (_, record) =>
            renderDateColumn(record, SubmittedForL4PlanDate, ['subForL4ApprovDate'], colorByAttribute, initiativeColors);
          break;
        case 'Sub . for L5 approv . date':
          newCol.sorter = safeSorter(SubmittedForL5PlanDate);
          newCol.render = (_, record) =>
            renderDateColumn(record, SubmittedForL5PlanDate, ['subForL5ApprovDate'], colorByAttribute, initiativeColors);
          break;
        case 'Cancellation date':
          newCol.sorter = safeSorter(getCancellationDate);
          newCol.render = (_, record) =>
            renderDateColumn(record, getCancellationDate, ['cancellationDate'], colorByAttribute, initiativeColors);
          break;
        case 'Number of attachments':
          newCol.sorter = (a, b) => {
            const aAttachment = a.attachmentCount || 0;
            const bAttachment = b.attachmentCount || 0;
            return aAttachment - bAttachment;
          };
          newCol.render = (_, record) => {
            const attachmentCount = record.attachmentCount || 0;
            const color = colorByAttribute === 'Number of Attachment' ? initiativeColors[attachmentCount] : null;
            return renderWithColor(attachmentCount, color, false);
          };
          break;
        case 'Has qualitative KPI?':
          newCol.sorter = (a, b) => {
            const aVal = getQualitativeKPI(a);
            const bVal = getQualitativeKPI(b);
            if (aVal === '<not set>' && bVal === '<not set>') return 0;
            if (aVal === '<not set>') return 1;
            if (bVal === '<not set>') return -1;
            return aVal.localeCompare(bVal);
          };
          newCol.render = (_, record) => {
            const financial = getQualitativeKPI(record);
            const color = colorByAttribute === 'Has qualitative KPI?' ? initiativeColors[financial] : null;
            return renderWithColor(financial, color, false);
          };
          break;
        case 'initiativeNumber - initiativeName':
          newCol.sorter = (a, b) => {
            const aNum = parseInt(a.initiativeNumber) || 0;
            const bNum = parseInt(b.initiativeNumber) || 0;
            return aNum - bNum;
          };
          newCol.render = (_, record) => {
            const nameNumber = `${record.initiativeNumber} - ${record.initiativeName}`;
            const color = colorByAttribute === 'initiativeNumber - initiativeName' ? initiativeColors[nameNumber] : null;
            return renderWithColor(nameNumber, color, true);
          };
          break;
        case 'selectedForToMeetingDescusion':
          newCol.filters = [{ text: 'Yes', value: true }, { text: 'No', value: false }];
          newCol.onFilter = (value, record) => record.selectedForToMeetingDescusion === value;
          newCol.sorter = (a, b) => {
            const valA = a.selectedForToMeetingDescusion ? 1 : 0;
            const valB = b.selectedForToMeetingDescusion ? 1 : 0;
            return valA - valB;
          };
          newCol.render = (value) => {
            let color = null;
            if (colorByAttribute === 'selectedForToMeetingDescusion') {
              color = initiativeColors[value ? 'Yes' : 'No'];
            }
            return renderWithColor(value ? 'Yes' : 'No', color, false);
          };
          break;
        case 'Net recurring benefit range':
          newCol.sorter = (a, b) => getNetRecurringBenefitCategory(a).localeCompare(getNetRecurringBenefitCategory(b));
          newCol.render = (_, initiative) => {
            const category = getNetRecurringBenefitCategory(initiative);
            let color = null;
            if (colorByAttribute === 'Net recurring benefits range (latest estimate, annualized)') {
              color = initiativeColors[category] || null;
            }
            return renderWithColor(category, color, false);
          };
          break;
        case 'Description':
          newCol.sorter = (a, b) => {
            return (a.description || '').localeCompare(b.description || '');
          };
          newCol.render = (_, record) => {
            const description = record.description || '';
            const color = colorByAttribute === 'Description' ? initiativeColors[description] : null;
            return renderWithColor(description, color, true);
          };
          break;
        default:
          if (newCol.sorter === undefined) {
            newCol.sorter = (a, b) => {
              const valA = a[newCol.dataIndex];
              const valB = b[newCol.dataIndex];
              if (typeof valA === 'number' && typeof valB === 'number') {
                return valA - valB;
              }
              return String(valA || '').localeCompare(String(valB || ''));
            };
          }
          if (newCol.render === undefined) {
            newCol.render = (text) => {
              const safeText = safeToString(text);
              const color = colorByAttribute === col.key ? initiativeColors[safeText] : null;
              return renderWithColor(safeText, color, false);
            };
          }
          break;
      }

      return newCol;
    });
  }, [initiatives, colorByAttribute, initiativeColors, setCurrentFilter, setFilterVisible, setFilterHistory, renderWithColor, columnWidths, handleResize, safeToString, sortField, renderDateColumn]);

  const [dynamicPadding, setDynamicPadding] = useState(20);

  useEffect(() => {
    const calculatePadding = () => {
      if (otherElementRef && otherElementRef.current) {
        setDynamicPadding(otherElementRef.current.offsetWidth + 20);
      } else {
        setDynamicPadding(20);
      }
    };

    calculatePadding();
    window.addEventListener('resize', calculatePadding);
    return () => window.removeEventListener('resize', calculatePadding);
  }, [otherElementRef]);

  // Components for resize functionality
  const components = {
    header: {
      cell: ResizableTitle,
    },
  };

  // Filter columns based on visibleColumn
  const displayedColumns = useMemo(() => {
    return columns.filter(col => visibleColumn.includes(col.key) || col.key === colorByAttribute);
  }, [columns, visibleColumn, colorByAttribute]);

  // Get current sort order for the table
  const getCurrentSortOrder = () => {
    if (sortField && sortOrder) {
      return {
        field: sortField,
        order: sortOrder,
        columnKey: sortField,
        column: columns.find(col => col.key === sortField)
      };
    }
    return null;
  };

  return (
    <div style={{ overflowX: 'auto', width: '100%' }}>
      <Table
        components={components}
        columns={displayedColumns}
        dataSource={myInitiatives}
        scroll={{ x: 'max-content' }}
        style={{ width: '100%' }}
        size="middle"
        className="max-w-full overflow-x-auto hoverable-table"
        pagination={{
          current: currentPage,
          pageSize: 20,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          onChange: (page) => {
            setCurrentPage(page);
            history.push(`/?section=workbook&page=${page}`);
          },
        }}
        onChange={handleTableChange}
        sortDirections={['ascend', 'descend', 'ascend']}
        sortOrder={sortOrder ? sortOrder : null}
        sortField={sortField ? sortField : null}
        onRow={(record) => ({
          onClick: () => {
            const isOwner = record?.initiativeOwnerInfo?.[0]?._id === currentUser.id;
            const isBackupOwner = record?.initiativeOwnerBackupInfo?.[0]?._id === currentUser.id;
            const isWorkstreamLead = record?.workstreamLeadInfo?.[0]?._id === currentUser.id;
            const isWorkstreamSponsor = record?.workstreamSponsorInfo?.[0]?._id === currentUser.id;
            const isAllowedUser = record?.AllowOnlyTo?.some(u => u._id === currentUser.id);
            const isAdminRole = ['CTO', 'TO Finance', 'Strategy Analyst', 'Admin'].includes(currentUser.role?.name);

            const hasInitiativeLevelAccess =
              isOwner ||
              isBackupOwner ||
              isWorkstreamLead ||
              isWorkstreamSponsor ||
              isAllowedUser ||
              isAdminRole;

            if (hasInitiativeLevelAccess) {
              const encryptedId = cryptoHelper.encrypt(record._id);
              const encodedData = encryptedId.replace(/\//g, '_');
              history.push({
                pathname: `initiative/update/${encodedData}`,
                state: { returnPage: currentPage }
              });
            } else {
              console.warn("Access Denied: You only have permission to view your assigned milestones, not the initiative details.");
            }
          },
        })}
        rowKey='_id'
      />
    </div>
  );
};

export default TableView;